import { useEffect } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, useMap } from 'react-leaflet';
import L, { type LatLngExpression } from 'leaflet';

const iconUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png';
const iconRetinaUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png';
const shadowUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png';

// Leaflet's default Webpack-era resolver fails under Vite; wire icons explicitly once.
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

const pickupIcon = L.divIcon({
  className: '',
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#111827;border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const dropIcon = L.divIcon({
  className: '',
  html: '<div style="width:14px;height:14px;border-radius:3px;background:#2BD4A7;border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const driverIcon = L.divIcon({
  className: '',
  html: '<div style="position:relative;width:18px;height:18px"><span style="position:absolute;inset:0;border-radius:50%;background:rgba(43,212,167,0.35);animation:raahi-pulse 1.8s ease-out infinite"></span><span style="position:absolute;inset:3px;border-radius:50%;background:#FFFFFF;border:3px solid #111827;box-shadow:0 2px 6px rgba(0,0,0,0.25)"></span></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

if (typeof document !== 'undefined' && !document.getElementById('raahi-map-style')) {
  const style = document.createElement('style');
  style.id = 'raahi-map-style';
  style.textContent =
    '@keyframes raahi-pulse{0%{transform:scale(0.6);opacity:0.9}100%{transform:scale(2.2);opacity:0}}';
  document.head.appendChild(style);
}

interface MapViewProps {
  centre: { lat: number; lng: number };
  pickup?: { lat: number; lng: number };
  drop?: { lat: number; lng: number };
  drivers?: Array<{ id: string; lat: number; lng: number }>;
  /** Live driver marker — used when a trip is assigned. */
  liveDriver?: { lat: number; lng: number } | null;
  follow?: 'centre' | 'route' | 'driver';
  className?: string;
}

function ViewSync({ centre, pickup, drop, liveDriver, follow }: MapViewProps) {
  const map = useMap();
  useEffect(() => {
    if (follow === 'route' && pickup && drop) {
      map.fitBounds(
        [
          [pickup.lat, pickup.lng],
          [drop.lat, drop.lng],
        ],
        { padding: [40, 40] },
      );
      return;
    }
    if (follow === 'driver' && liveDriver) {
      map.panTo([liveDriver.lat, liveDriver.lng]);
      return;
    }
    map.setView([centre.lat, centre.lng], map.getZoom() || 13);
  }, [map, centre.lat, centre.lng, pickup?.lat, pickup?.lng, drop?.lat, drop?.lng, liveDriver?.lat, liveDriver?.lng, follow]);
  return null;
}

export function MapView(props: MapViewProps) {
  const { centre, pickup, drop, drivers, liveDriver, className } = props;
  const centrePos: LatLngExpression = [centre.lat, centre.lng];

  return (
    <MapContainer center={centrePos} zoom={13} zoomControl={false} className={className ?? 'h-full w-full'}>
      <TileLayer
        attribution='&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <ViewSync {...props} />
      {pickup ? <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon} /> : null}
      {drop ? <Marker position={[drop.lat, drop.lng]} icon={dropIcon} /> : null}
      {pickup && drop ? (
        <Polyline
          positions={[
            [pickup.lat, pickup.lng],
            [drop.lat, drop.lng],
          ]}
          pathOptions={{ color: '#111827', weight: 3, opacity: 0.85, dashArray: '6 8' }}
        />
      ) : null}
      {drivers?.map((d) => (
        <Marker key={d.id} position={[d.lat, d.lng]} icon={driverIcon} />
      ))}
      {liveDriver ? <Marker position={[liveDriver.lat, liveDriver.lng]} icon={driverIcon} /> : null}
    </MapContainer>
  );
}
